import { useSearchParams } from 'react-router-dom';

import { MapScope } from '@/graphql/types';
import Button from '../../form/Button';
import { BasePage } from '../BasePage';
import { ErrorPage } from '../ErrorPage';
import { LoadingPage } from '../LoadingPage';
import LoadingSpinner from '../../loadingSpinner/LoadingSpinner';
import { useProfile } from '../../../profile';
import { GoogleMapCanvas } from './components/GoogleMapCanvas';
import { useMapMarkersQuery } from './graphql/useMapMarkersQuery';
import { useMapPageActions } from './useMapPageActions';

interface MapPageProps {
    scope: MapScope;
}

export function MapPage({ scope }: MapPageProps) {
    const profile = useProfile();
    const [searchParams, setSearchParams] = useSearchParams();
    const useDeviceGps = searchParams.get('useDeviceGps') === 'true';
    const { loading, isLoadingMore, error, markers } = useMapMarkersQuery({
        scope,
        useDeviceGps,
    });
    const legacyMeasuringPointId = searchParams.get('measuringPointId');
    const measuringPointIds = searchParams.getAll('measuringPointIds');

    if (legacyMeasuringPointId) {
        measuringPointIds.push(legacyMeasuringPointId);
    }

    const selectedMeasuringPointIds = new Set(measuringPointIds);

    function toggleDeviceGps() {
        const nextSearchParams = new URLSearchParams(searchParams);

        if (useDeviceGps) {
            nextSearchParams.delete('useDeviceGps');
        } else {
            nextSearchParams.set('useDeviceGps', 'true');
        }

        setSearchParams(nextSearchParams);
    }

    const visibleMarkers =
        selectedMeasuringPointIds.size > 0
            ? markers.filter((marker) =>
                  selectedMeasuringPointIds.has(String(marker.measuringPointId))
              )
            : markers;
    const { onMarkerDoubleClick, renderInfoWindowContent } = useMapPageActions({
        scope,
        userId: profile.userId,
    });

    if (loading) {
        return <LoadingPage />;
    }

    if (error) {
        return <ErrorPage error={error} />;
    }

    return (
        <BasePage
            title={gettext('Map')}
            besideTitle={
                <Button
                    type="button"
                    variant="primary"
                    className="px-6 py-2 text-sm"
                    onClick={toggleDeviceGps}
                >
                    {useDeviceGps
                        ? gettext('MAP_USE_MEASURING_POINT_LOCATION')
                        : gettext('MAP_USE_DEVICE_GPS')}
                </Button>
            }
        >
            {scope === MapScope.Admin && (
                <form id="map-hijack-form" action="/hijack/0/" method="post" className="hidden">
                    <input
                        type="hidden"
                        name="csrfmiddlewaretoken"
                        value={profile.csrfToken ?? ''}
                    />
                </form>
            )}

            <div className="relative">
                {isLoadingMore && (
                    <div className="absolute left-1/2 top-3 z-[1] flex -translate-x-1/2 items-center gap-2 rounded-md bg-white/90 px-3 py-1.5 text-sm shadow">
                        <LoadingSpinner />
                        <span>{gettext('LOADING')}</span>
                    </div>
                )}

                <GoogleMapCanvas
                    markers={visibleMarkers}
                    onMarkerDoubleClick={onMarkerDoubleClick}
                    renderInfoWindowContent={renderInfoWindowContent}
                />
            </div>
        </BasePage>
    );
}
